/**
 * Read the HTTP status off an error thrown by the generated `@devour/client`.
 *
 * WHY THIS EXISTS: openapi-generator 7.14.0 (shipped 2026-08-16) changed what
 * the client throws on a non-2xx response. It used to throw the raw `Response`,
 * which carries `.status` directly. It now throws a `ResponseError` wrapper
 * whose only own property is `response` — there is NO `.status` on it:
 *
 *     export class ResponseError extends Error {
 *         override name: "ResponseError" = "ResponseError";
 *         constructor(public response: Response, msg?: string) { super(msg); }
 *     }
 *
 * Every `if (e.status === 4xx)` written against the old client therefore
 * compares `undefined` and silently takes the wrong branch. That is not
 * hypothetical: it killed the lootshop cart-recreate path in
 * `updateMenuOrder.ts`, so a stale cart surfaced to users as "An unknown error
 * has occurred. Please contact support" instead of quietly rebuilding
 * (Sentry DEVOUR-WEB-3RB / DEVOUR-WEB-3MF, confirmed in production with
 * `errorName: "ResponseError"` and `httpStatus: 403`).
 *
 * Nothing normalises this for us — `getConfig()` registers no client
 * middleware, and the React Query `onError` handler in DevourProviders reads
 * the pre-upgrade shape too.
 *
 * The raw-`Response` branch is kept deliberately: browsers holding a cached
 * pre-upgrade bundle still throw the old shape, and dropping it would break
 * them for as long as that cache lives.
 */
export function getApiErrorStatus(error: unknown): number | undefined {
    if (error == null || typeof error !== "object") {
        return undefined;
    }

    // Current client: ResponseError — the status lives on the wrapped Response.
    const wrapped = (error as { response?: { status?: unknown } }).response;
    if (wrapped && typeof wrapped.status === "number") {
        return wrapped.status;
    }

    // Legacy client (and any raw Response): status sits directly on the error.
    const direct = (error as { status?: unknown }).status;
    if (typeof direct === "number") {
        return direct;
    }

    return undefined;
}
